import _ from "lodash";
import React, { useRef, useEffect } from "react";
import classes from "./GlobalSearchBarV3.scss";
import I18nHOC from "UIComponents/I18nHOC";
import { withCombinedAccessBlocker } from "lib/PermissionAware";
import { withRouter } from "react-router";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import { useKey } from "react-use";
import SearchInput from "./SearchInput";
import RecentSearch from "./RecentSearch";
import SearchResultsComp from "./SearchResult";
import ModulesSection from "./ModulesSection";
import Suggestions from "./Suggestions";
import Footer from "./Footer";
import {
  typesConfig,
  getModulesFiltersList,
  getRecentSearchItems,
  getIsAllCurriculumSelected,
  ALL_CURRICULUM_INDICATOR,
  getIsProjectGroupSearchEnabled,
  getIsClassSearchV3Enabled,
  getVariablesForGlobalSearchPermissions,
  getCoursesV2QueryFiltersMemoized,
} from "./Utils";
import useLocalSuggestions from "./useLocalSuggestions";
import { navigate } from "modules/NavigationModule";

import {
  getUbdAssessmentTypesMultiNodeQuery,
  getUserCoursesV2Query,
} from "../modules/GlobalSearchBarQueries";
import { getConfigurationQueryEnhancer } from "ChildrenPage/modules/ChildrenPageEnhancers";
import {
  getConfigurationQueryVariable,
  globalSearchConfigurationSelector,
} from "../modules/GlobalSearchBarModule";
import {
  updateGlobalSearchText,
  toggleVoiceSearch,
  toggleGlobalSearchBarModal,
  updateGlobalSearchModuleFilter,
  updateGlobalSearchCoursesContext,
  updateGlobalSearchCurriculumContext,
  updateUbdAssessmentTypes,
} from "modules/Services";
import { recordEvent } from "lib/eventTracker";
import ACLStore from "lib/aclStore";
import { changeLocalSearchStorageData } from "services/localStorage";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { authorizeUserWithEsfSentral } from "Login/modules/LoginModule";
import { Modal } from "@toddle-edu/ds-web";
import Loading from "UIComponents/Loading";
import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
  NODE_TYPE_UBD_CURRICULUM_PROGRAM,
  PROJECT_SUBTYPE_CORE,
  PROJECT_SUBTYPE_SUBJECT_GROUP_PROJECT,
} from "Constants/stringConstants";
import { GLOBAL_SEARCH_CHIPS_FEATURE_FLAGS_MAPPINGS } from "Constants/permissionConstants";
import { getUbdAssessmentTypes } from "Platform/modules/PlatformUtils";
import {
  getProjectGroupCategoriesQuery,
  getUserProjectGroupsQuery,
} from "Teacher/routes/Projects/modules/ProjectQueries";
import {
  getAcademicYearId,
  getActiveTab,
  getIsStaff,
} from "modules/CommonSelectors";
import { getUserProjectGroupsFromCache } from "Teacher/routes/Projects/modules/ProjectGraphqlHelpers";
import { getRnPQueryVariablesForProjectGroupCategories } from "Teacher/components/TeacherUtils";
import { getAvailableProjectGroupTypesForAllCurriculums } from "Platform/components/GlobalHeader/modules/Utils";
import withControlParameters from "lib/aclService/hoc/withControlParameters";

const loaderStyles = {
  containerStyle: {
    height: "100%",
    width: "100%",
  },
  modalBodyStyle: {
    paddingBottom: 0,
  },
};

/**
 * Here, pseudo interaction keys implies that
 * they don't have any functionality wired to them,
 * however when user presses these key, we don't want to
 * return focus back to search input.
 */

const INTERACTION_KEYS = [
  "ArrowUp",
  "ArrowDown",
  "ArrowRight",
  "ArrowLeft",
  "Enter",
  "Tab",
  "Shift",
];

const SENTRAL_FEATURE_TYPES = [
  typesConfig.ESF_SENTRAL_WELLBEING.id,
  typesConfig.ESF_SENTRAL_ATTENDANCE.id,
  typesConfig.ESF_SENTRAL_POLICIES.id,
  typesConfig.ESF_SENTRAL_TIMETABLE.id,
  typesConfig.ESF_SENTRAL_PXP.id,
  typesConfig.ESF_SENTRAL_NEWSFEED.id,
  typesConfig.ESF_SENTRAL_DAILY_NOTICE.id,
];

const LOCAL_SEARCH_STORAGE_COUNT = 25;
const SEARCH_MODULE_NAME = "searches";

const GlobalSearchBarV3 = props => {
  const {
    showSearchBar,
    toggleGlobalSearchBarModal,
    enableVoiceSearch,
    toggleVoiceSearch,
    userId,
    userInfo,
    navigate,
    searchText,
    module,
    updateGlobalSearchText,
    updateGlobalSearchCoursesContext,
    updateGlobalSearchCurriculumContext,
    t,
    courses,
    shouldUpdateCourses,
    curriculumProgramType,
    orgId,
    curriculumPrograms,
    selectedCurriculum,
    platformCurriculumProgramId,
    modalBodyStyle,
    updateGlobalSearchModuleFilter,
    availableProjectGroupTypes = [],
    isData,
    isLoading,
    updateUbdAssessmentTypes,
    ubdAssessmentTypes,
    isAllCurriculumSelected,
    loginActiveTab,
    selectedCurriculumProgramTypes,
    authorizeUserWithEsfSentral,
  } = props;

  const searchInputRef = useRef();
  const modalContainerRef = useRef();
  const curriculumDropdownRef = useRef(null);

  const recentSearchResults = getRecentSearchItems({
    userId,
    module,
    userInfo,
    t,
    selectedCurriculum,
    selectedCurriculumProgramTypes,
  });

  const modulesList = getModulesFiltersList({
    t,
    userInfo,
    curriculumProgram: selectedCurriculum,
    useV2List: true,
  });

  const searchTextRef = useRef(searchText);

  useEffect(() => {
    searchTextRef.current = searchText;
  }, [searchText]);

  const updateCurriculumContext = () => {
    let selectedCurriculum = _.find(curriculumPrograms, {
      id: platformCurriculumProgramId,
    });

    if (!selectedCurriculum) {
      selectedCurriculum =
        _.size(curriculumPrograms) > 1 && loginActiveTab == "admin"
          ? {
              id: ALL_CURRICULUM_INDICATOR,
            }
          : _.first(curriculumPrograms);
    }

    updateGlobalSearchCurriculumContext({
      selectedCurriculum,
    });
  };

  useEffect(() => {
    // if selected curriculum is null or search text is empty
    // determine curriculum context from the curriculumPrograms list
    // else retain the curriculum
    if (
      !(
        searchText &&
        selectedCurriculum?.id &&
        (_.includes(curriculumPrograms, selectedCurriculum) ||
          isAllCurriculumSelected)
      )
    ) {
      updateCurriculumContext();
    }

    // Reset the search text
    return () => {
      const trimmedSearchText = _.trim(searchTextRef.current ?? "");
      if (!trimmedSearchText) {
        updateGlobalSearchModuleFilter(null);
        onSearchInputChange();
      }
    };
  }, []);

  const setInputFocus = () => {
    searchInputRef.current?.focus?.();
  };

  useEffect(() => {
    setInputFocus();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const modalContainer = modalContainerRef.current;
      if (modalContainer) {
        /** 
         * simulate a real click sequence
         * Why?
            - Some components like dropdowns and popovers stay open until they detect an outside click.
            - Programmatic .focus() does not trigger those outside-click handlers.
            - By imitating a physical click sequence on our search input, we ensure:
                1. The input is focused and ready for typing.
                2. Any open dropdowns or popovers in the background will register the event
                    as a click outside themselves and close automatically.
        */

        modalContainer.focus();
        modalContainer.dispatchEvent(
          new MouseEvent("mousedown", { bubbles: true })
        );
        modalContainer.dispatchEvent(
          new MouseEvent("mouseup", { bubbles: true })
        );
        modalContainer.dispatchEvent(
          new MouseEvent("click", { bubbles: true })
        );
      }
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  const previousUbdAssessmentTypesRef = useRef(null);

  useEffect(() => {
    const currentUbdAssessmentTypes = JSON.stringify(ubdAssessmentTypes);

    if (
      (!previousUbdAssessmentTypesRef.current ||
        previousUbdAssessmentTypesRef.current !== currentUbdAssessmentTypes) &&
      ubdAssessmentTypes
    ) {
      updateUbdAssessmentTypes({
        ubdAssessmentTypes,
      });
      previousUbdAssessmentTypesRef.current = currentUbdAssessmentTypes;
    }
  }, [ubdAssessmentTypes]);

  const onSearchInputChange = (value = "") => {
    updateGlobalSearchText(value);

    recordEvent({
      eventName: "GLOBAL_SEARCH",
      eventData: {
        searchText: value,
        event_type: "SEARCH",
      },
    });
  };

  const onCurriculumChange = selectedProgram => {
    if (selectedCurriculum?.id === selectedProgram?.id) return;
    updateGlobalSearchCurriculumContext({
      selectedCurriculum: selectedProgram,
    });
  };

  const onSelectModule = ({ type }) => {
    updateGlobalSearchModuleFilter(type);

    setInputFocus();
  };

  const onUnselectModule = () => {
    updateGlobalSearchModuleFilter(null);
    setInputFocus();
  };

  const onSuggestionClick = ({ item, e }) => {
    const type = item.type;
    // doing this as the item is being stringified in navigate and local storage and the subtitle can be a react element which causes the stringify to fail
    const subTitle = React.isValidElement(item.subTitle) ? "" : item.subTitle;
    const params = {
      ...item,
      type,
      subTitle,
      source: "GLOBAL_SEARCH",
    };
    let shouldNavigate = true;
    const routerParams = props.router.params;

    const isMouseClick = !!e.isTrusted;
    recordEvent({
      eventName: "GLOBAL_SEARCH",
      eventData: {
        clicked_item: type,
        searchText,
        filter_module: module,
        trigger: isMouseClick ? "MOUSE_CLICK" : "KEYBOARD",
      },
    });

    /**
     * @description unit plan details tab data doesn't render
     * when same unit plan is selected again.
     */
    if (
      routerParams?.course_id == item?.metadata?.courseId &&
      routerParams?.unit_id == item?.metadata?.unitPlanId &&
      item.type == typesConfig.UNIT_PLAN.id
    ) {
      shouldNavigate = false;
    }

    toggleGlobalSearchBarModal();

    //navigate to that particular module
    if (SENTRAL_FEATURE_TYPES.includes(type)) {
      authorizeUserWithEsfSentral({ feature: type });
    } else if (shouldNavigate) {
      navigate({ params });
    }
    //change in local storage data
    changeLocalSearchStorageData({
      data: params,
      userId,
      searchModuleName: SEARCH_MODULE_NAME,
      localSearchStorageCount: LOCAL_SEARCH_STORAGE_COUNT,
    });
  };

  const handleEnterKey = e => {
    e.stopPropagation();
    const currentFocusedElement = modalContainerRef.current?.querySelector(
      ":focus"
    );

    const curriculumDropdownElement = curriculumDropdownRef?.current;

    const isFocusOnCurriculumDropdown = _.isEqual(
      curriculumDropdownElement,
      currentFocusedElement
    );

    if (isFocusOnCurriculumDropdown) {
      return;
    }

    const currentFocusedElementTestId = currentFocusedElement?.getAttribute(
      "test-id"
    );

    if (
      _.isEqual(
        currentFocusedElementTestId,
        "globalSearch-searchResults-seeMore"
      )
    ) {
      const focusableElements = [
        ...(modalContainerRef.current?.querySelectorAll(
          "[data-elm=search-item]"
        ) ?? []),
      ];

      const focusedElementIndex = _.findIndex(
        focusableElements,
        currentFocusedElement
      );
      focusableElements[focusedElementIndex - 1]?.focus();
    }
    currentFocusedElement?.click();
    return;
  };

  const focusNextElement = (direction = "up") => e => {
    e.preventDefault();

    const focusableElements = [
      ...(modalContainerRef.current?.querySelectorAll(
        "[data-elm=search-item]"
      ) ?? []),
    ];

    const currentFocusedElement = modalContainerRef.current?.querySelector(
      ":focus"
    );

    const isFocusInsideDropdown =
      !_.isEqual(curriculumDropdownRef?.current, currentFocusedElement) &&
      curriculumDropdownRef?.current?.contains(document.activeElement);

    if (isFocusInsideDropdown) {
      return;
    }

    const currentFocusedElementIndex = _.findIndex(
      focusableElements,
      currentFocusedElement
    );

    const lastIndex = focusableElements.length - 1;

    if (currentFocusedElementIndex === -1 || currentFocusedElement === null) {
      if (direction === "up") {
        focusableElements[lastIndex]?.focus();
      } else if (direction === "down") {
        focusableElements[0]?.focus();
      }
      return;
    }

    let nextFocusElementIndex;

    if (direction === "up") {
      nextFocusElementIndex =
        currentFocusedElementIndex === 0
          ? lastIndex
          : currentFocusedElementIndex - 1;
    } else if (direction === "down") {
      nextFocusElementIndex =
        currentFocusedElementIndex === lastIndex
          ? 0
          : currentFocusedElementIndex + 1;
    } else {
      console.error("invalid direction", direction);
      return;
    }

    focusableElements[nextFocusElementIndex]?.focus();
    setTimeout(() => {
      // select the texts in the element if the element is input element
      focusableElements[nextFocusElementIndex]?.select?.();
    }, 0);
  };

  useEffect(() => {
    setInputFocus();
  }, [selectedCurriculum?.id]);

  useKey("ArrowUp", focusNextElement("up"), {}, []);
  useKey("ArrowDown", focusNextElement("down"), {}, []);

  useKey("Enter", handleEnterKey, {}, []);

  useKey(
    event => {
      const { key } = event;
      return !_.includes(INTERACTION_KEYS, key);
    },
    setInputFocus,
    {},
    []
  );

  if (!showSearchBar) {
    return null;
  }

  return (
    <Modal
      dsVersion="2.0"
      width="888px"
      height="588px"
      isOpen={true}
      onClose={toggleGlobalSearchBarModal}
    >
      <Modal.Body style={modalBodyStyle} isScrollable>
        <div ref={modalContainerRef} className={classes.container}>
          <div className={classes.topContainer}>
            <SearchInput
              onChange={onSearchInputChange}
              module={module}
              onUnselectModule={onUnselectModule}
              enableVoiceSearch={enableVoiceSearch}
              toggleVoiceSearch={toggleVoiceSearch}
              ref={searchInputRef}
              searchText={searchText}
              curriculumPrograms={curriculumPrograms}
              selectedCurriculum={selectedCurriculum}
              onCurriculumChange={onCurriculumChange}
              curriculumDropdownRef={curriculumDropdownRef}
              platformCurriculumProgramId={platformCurriculumProgramId}
            />
            <ModulesSection
              data={modulesList}
              isData={isData}
              isLoading={isLoading}
              onSelectModule={onSelectModule}
              onUnselectModule={onUnselectModule}
              selectedModule={module}
            />
          </div>

          <div className={classes.focusZoneContainer}>
            {!searchText ? (
              !module ? (
                <RecentSearch
                  data={recentSearchResults}
                  module={module}
                  onSuggestionClick={onSuggestionClick}
                />
              ) : _.size(recentSearchResults) ? (
                <RecentSearch
                  data={recentSearchResults}
                  module={module}
                  onSuggestionClick={onSuggestionClick}
                />
              ) : (
                <div className={classes.suggestions}>
                  <Suggestions
                    module={module}
                    courses={courses}
                    onSuggestionClick={onSuggestionClick}
                  />
                </div>
              )
            ) : null}

            {isData && searchText ? (
              <SearchResults
                searchText={searchText}
                onSuggestionClick={onSuggestionClick}
                module={module}
                selectedCurriculum={selectedCurriculum}
                t={t}
                orgId={orgId}
                courses={courses}
                availableProjectGroupTypes={availableProjectGroupTypes}
                curriculumProgramType={curriculumProgramType}
                shouldUpdateCourses={shouldUpdateCourses}
                updateGlobalSearchCoursesContext={
                  updateGlobalSearchCoursesContext
                }
              />
            ) : null}

            {searchText && isLoading && !isData ? (
              <Loading {...loaderStyles} />
            ) : null}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer hasSeparator>
        <Footer />
      </Modal.Footer>
    </Modal>
  );
};

const isGlobalSearchCourseExpired = updatedAt => {
  const tenMinuteMilliSeconds = 1000 * 60 * 30;
  const diff = Date.now() - updatedAt;
  return diff > tenMinuteMilliSeconds;
};

const getUserCurriculumCourses = ({ courses, curriculumIds = [] }) => {
  if (_.isEmpty(curriculumIds)) {
    return courses;
  }

  const filteredCourses = _.filter(courses, course => {
    const courseCurriculumId = _.get(course, "curriculumProgram.id", null);
    return _.includes(curriculumIds, courseCurriculumId);
  });

  return filteredCourses;
};

const getUBDAssessmentTypesMultiNodeVariables = ({
  userCurriculumPrograms,
}) => {
  const ubdCurriculumProgramIds = _.reduce(
    userCurriculumPrograms,
    (result, curriculum) => {
      if (curriculum.type === CURRICULUM_TYPE_UBD) {
        result.push(curriculum.id);
      }
      return result;
    },
    []
  );

  return {
    ids: ubdCurriculumProgramIds,
    type: NODE_TYPE_UBD_CURRICULUM_PROGRAM,
  };
};

const getUBDAssessmentTypesMultiNodeVariablesMemoized = _.memoize(
  params => getUBDAssessmentTypesMultiNodeVariables(params),
  params => JSON.stringify(params)
);

const getYearGroupCurriculumProgramFilterMemoized = _.memoize(
  curriculumProgramIds => {
    return !ACLStore.can("FeatureFlag:ShowAllYearGroupProjectGroups")
      ? {
          yearGroupCurriculumProgramIds: curriculumProgramIds,
        }
      : {};
  },
  JSON.stringify
);

const BLANK_ARRAY = [];

const staffProjectGroupSubTypeFilters = [
  PROJECT_SUBTYPE_CORE,
  PROJECT_SUBTYPE_SUBJECT_GROUP_PROJECT,
];

const getAvailableProjectGroupTypesForAllCurriculumsMemoized = _.memoize(
  params => getAvailableProjectGroupTypesForAllCurriculums(params),
  params => JSON.stringify(params)
);

const mapStateToProps = state => {
  const userInfo = getUserInfo();
  const {
    id: userId,
    userEntityType,
    org_id: orgId,
    userCurriculumPrograms: curriculumPrograms,
  } = userInfo;

  const curriculumProgramType = _.uniq(_.map(curriculumPrograms, "type"));
  const targetCurriculumIds = _.map(curriculumPrograms, "id");

  const platformCurriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id"
  );

  const searchText = _.get(
    state,
    "app_services.globalSearchBar.searchText",
    ""
  );

  const showSearchBar = _.get(
    state,
    "app_services.globalSearchBar.showSearchBar",
    false
  );
  const enableVoiceSearch = _.get(
    state,
    "app_services.globalSearchBar.enableVoiceSearch",
    false
  );
  const globalSearchCourses = _.get(
    state,
    "app_services.globalSearchBar.courses.allCourses"
  );
  const globalSearchCourseUpdatedAt = _.get(
    state,
    "app_services.globalSearchBar.courses.updatedAt",
    null
  );

  const module = _.get(state, "app_services.globalSearchBar.module", null);

  const shouldUpdateCourses =
    globalSearchCourses === null ||
    globalSearchCourseUpdatedAt === null ||
    isGlobalSearchCourseExpired(globalSearchCourseUpdatedAt);

  const userCourses = getUserCurriculumCourses({
    courses: globalSearchCourses,
    curriculumIds: targetCurriculumIds,
  });

  const selectedCurriculum = _.get(
    state,
    "app_services.globalSearchBar.selectedCurriculum",
    ""
  );

  const contextForFetchingPermBasedOnRnp = [
    { entityId: selectedCurriculum?.id, entityType: "CURRICULUM_PROGRAM" },
  ];

  const configurationQueryVariables = getConfigurationQueryVariable({
    contexts: contextForFetchingPermBasedOnRnp,
  });

  const ubdAssessmentTypesMultiNodeVariables = getUBDAssessmentTypesMultiNodeVariablesMemoized(
    { userCurriculumPrograms: curriculumPrograms }
  );

  const currentAcademicYearId = getAcademicYearId(state);
  const portalType = "PLANNER";
  const isStaff = getIsStaff(state);
  const projectGroupSubTypeFilters = isStaff
    ? staffProjectGroupSubTypeFilters
    : BLANK_ARRAY;

  const isAllCurriculumSelected = getIsAllCurriculumSelected(
    selectedCurriculum?.id
  );

  const curriculumProgramIds = isAllCurriculumSelected
    ? targetCurriculumIds
    : [selectedCurriculum?.id];

  const selectedCurriculumProgramTypes = isAllCurriculumSelected
    ? curriculumProgramType
    : [selectedCurriculum?.type];

  const yearGroupCurriculumProgramFilter = getYearGroupCurriculumProgramFilterMemoized(
    curriculumProgramIds
  );

  const loginActiveTab = getActiveTab(state);

  const isProjectGroupSearchEnabled = getIsProjectGroupSearchEnabled();

  const isInvalidCurriculumId = _.some(curriculumProgramIds, _.isNil);

  const userCurriculumPrograms = userInfo.userCurriculumPrograms;

  const userCurriculumProgramsIds = _.map(userCurriculumPrograms, "id");

  const coursesV2QueryFilters = getCoursesV2QueryFiltersMemoized({
    userId,
    userCurriculumProgramsIds,
  });

  const projectCurriculumIds = _.map(
    _.filter(userCurriculumPrograms, curriculum =>
      _.includes([CURRICULUM_TYPE_DP, CURRICULUM_TYPE_MYP], curriculum.type)
    ),
    "id"
  );

  return {
    accessPermissions: [
      "TeacherPortal:GlobalSearchBar",
      "StudentPortal:GlobalSearchBar",
    ],
    errorText: "Do not have permission to see this page",
    showErrorComp: false,
    orgId,
    userId,
    userEntityType,
    userInfo,
    searchText,
    showSearchBar,
    enableVoiceSearch,
    module,
    shouldUpdateCourses,
    configurationQueryVariables,
    courses: userCourses,
    curriculumProgramType,
    isData: true,
    isLoading: false,
    curriculumPrograms,
    selectedCurriculum,
    platformCurriculumProgramId,
    ubdAssessmentTypesMultiNodeVariables,
    curriculumProgramIds,
    yearGroupCurriculumProgramFilter,
    portalType,
    currentAcademicYearId,
    projectGroupSubTypeFilters,
    isAllCurriculumSelected,
    loginActiveTab,
    isProjectGroupSearchEnabled,
    isInvalidCurriculumId,
    selectedCurriculumProgramTypes,
    coursesV2QueryFilters,
    userCurriculumPrograms,
    projectCurriculumIds,
  };
};

const mapActionCreators = {
  navigate,
  updateGlobalSearchText,
  toggleVoiceSearch,
  toggleGlobalSearchBarModal,
  updateGlobalSearchModuleFilter,
  updateGlobalSearchCoursesContext,
  updateGlobalSearchCurriculumContext,
  updateUbdAssessmentTypes,
  authorizeUserWithEsfSentral,
};

export default compose(
  withRouter,
  I18nHOC({
    resource: [
      "common",
      "teacherHomePage",
      "classRoom",
      "configuration",
      "attendance",
      "integration",
      "snp",
    ],
  }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getUserCoursesV2Query, {
    name: "getUserCoursesV2Query",
    skip: ({ accessPermissions, shouldUpdateCourses }) => {
      return (
        getIsClassSearchV3Enabled() ||
        !ACLStore.can(accessPermissions) ||
        !shouldUpdateCourses
      );
    },
    options: ({ coursesV2QueryFilters }) => ({
      variables: coursesV2QueryFilters,
      fetchPolicy: "cache-and-network",
    }),
    props: ({ getUserCoursesV2Query, ownProps: { isData, isLoading } }) => {
      const queryResult = _.get(getUserCoursesV2Query, "node", {});
      const courseEdges = _.get(queryResult, "coursesV2.edges", []);
      const courses = _.map(courseEdges, "node");
      const networkStatus = _.get(getUserCoursesV2Query, "networkStatus");
      return {
        courses,
        isData: !_.isEmpty(queryResult) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getUbdAssessmentTypesMultiNodeQuery, {
    name: "getUbdAssessmentTypesMultiNode",
    skip: ({
      ubdAssessmentTypesMultiNodeVariables: { ids: ubdCurriculumIds = [] },
    }) =>
      _.isEmpty(ubdCurriculumIds) ||
      !ACLStore.can(GLOBAL_SEARCH_CHIPS_FEATURE_FLAGS_MAPPINGS.TASKS_SEARCH),
    options: ({ ubdAssessmentTypesMultiNodeVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: ubdAssessmentTypesMultiNodeVariables,
    }),
    props({ getUbdAssessmentTypesMultiNode, ownProps: { isData, isLoading } }) {
      const queryData = _.get(
        getUbdAssessmentTypesMultiNode,
        "multiNode",
        null
      );

      const ubdAssessmentTypes = _.reduce(
        queryData ?? [],
        (result, node) => {
          result[node.id] = getUbdAssessmentTypes(node.assessmentTypes);
          return result;
        },
        {}
      );

      return {
        ubdAssessmentTypes,
        isData: !_.isEmpty(queryData) && isData,
        isLoading:
          _.includes([1, 2], getUbdAssessmentTypesMultiNode["networkStatus"]) ||
          isLoading,
      };
    },
  }),
  graphql(getUserProjectGroupsQuery, {
    name: "getUserProjectGroups",
    skip: ({
      userId,
      userEntityType,
      currentAcademicYearId,
      isProjectGroupSearchEnabled,
      isInvalidCurriculumId,
    }) =>
      !userId ||
      !userEntityType ||
      !currentAcademicYearId ||
      !isProjectGroupSearchEnabled ||
      isInvalidCurriculumId,
    options: ({
      userId,
      userEntityType,
      curriculumProgramIds,
      yearGroupCurriculumProgramFilter,
      portalType,
      currentAcademicYearId,
      projectGroupSubTypeFilters,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: userId,
        type: userEntityType,
        filters: {
          curriculumProgramIds,
          projectGroupSubTypes: projectGroupSubTypeFilters,
          ...yearGroupCurriculumProgramFilter,
          yearGroupArchivalState: "ALL",
          academicYears: currentAcademicYearId ? [currentAcademicYearId] : [],
        },
        portalType,
      },
    }),
    props({
      getUserProjectGroups: { variables, networkStatus },
      ownProps: { isData, isLoading },
    }) {
      const data = getUserProjectGroupsFromCache(variables);

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        projectGroups: _.get(data, "node.projectGroups", []),
      };
    },
  }),
  getConfigurationQueryEnhancer(globalSearchConfigurationSelector, {
    fetchPolicy: "cache-and-network",
  }),
  withControlParameters({
    options: ({ userCurriculumPrograms }) => {
      return {
        variables: getVariablesForGlobalSearchPermissions({
          curriculumPrograms: userCurriculumPrograms,
        }),
      };
    },
  }),
  graphql(getProjectGroupCategoriesQuery, {
    name: "getProjectGroupCategories",
    skip: ({ projectCurriculumIds }) => _.isEmpty(projectCurriculumIds),
    options: ({ orgId, projectCurriculumIds }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        organizationId: orgId,
        filters: {
          curriculumProgramIds: projectCurriculumIds,
          projectGroupSubTypes: [
            PROJECT_SUBTYPE_CORE,
            PROJECT_SUBTYPE_SUBJECT_GROUP_PROJECT,
          ],
        },
      },
    }),
    props({ getProjectGroupCategories, ownProps: { isData, isLoading } }) {
      const data = _.get(getProjectGroupCategories, "node", {});
      const networkStatus = _.get(getProjectGroupCategories, "networkStatus");

      const projectGroupCategories = _.get(data, "projectGroupCategories", []);
      const projectGroupCategoryIdByType = _.reduce(
        projectGroupCategories,
        (result, category) => {
          result[category.type] = category.id;
          return result;
        },
        {}
      );
      return {
        isData: !_.isEmpty(data) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        projectGroupCategories,
        projectGroupCategoryIdByType,
      };
    },
  }),
  withControlParameters({
    skip: ({ projectGroupCategories }) => _.isEmpty(projectGroupCategories),
    skipContextSetup: true,
    options: ({ projectGroupCategories }) => {
      const variables = getRnPQueryVariablesForProjectGroupCategories(
        projectGroupCategories
      );
      return {
        variables,
      };
    },

    getControlParameterBasedProps: ({ ownProps }) => {
      const { projectGroups, projectGroupCategoryIdByType } = ownProps;

      const availableProjectGroupTypes = getAvailableProjectGroupTypesForAllCurriculumsMemoized(
        {
          projectGroups,
          projectGroupCategoryIdByType,
        }
      );
      return {
        availableProjectGroupTypes,
      };
    },
  }),
  withCombinedAccessBlocker
)(GlobalSearchBarV3);

const SearchResults = props => {
  const {
    searchText,
    onSuggestionClick,
    module,
    selectedCurriculum,
    t,
    orgId,
    courses,
    curriculumProgramType,
    shouldUpdateCourses,
    updateGlobalSearchCoursesContext,
    availableProjectGroupTypes,
  } = props;

  const {
    localSuggestions,
    isLoading: isLocalSuggestionsLoading,
  } = useLocalSuggestions({
    t,
    module,
    searchText,
    orgId,
    courses,
    curriculumProgramType,
    availableProjectGroupTypes,
  });

  useEffect(() => {
    if (shouldUpdateCourses) {
      updateGlobalSearchCoursesContext({ courses });
    }
  }, []);

  return (
    <div className={classes.searchResults}>
      <SearchResultsComp
        onSuggestionClick={onSuggestionClick}
        term={searchText}
        module={module}
        localSuggestions={localSuggestions}
        isLoading={isLocalSuggestionsLoading}
        isData={!isLocalSuggestionsLoading}
        selectedCurriculum={selectedCurriculum}
      />
    </div>
  );
};
